import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles, CalendarCheck, Shield, SparklesIcon, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import { listPricingTiers } from "@/integrations/backend/pricing";
import { createCheckoutSession } from "@/integrations/backend/stripe";
import { useAuth } from "@/hooks/useAuth";

type Billing = "monthly" | "annual";
type Tier = {
  id: string;
  name: string;
  blurb: string;
  features: string[];
  price_monthly: number;
  price_annual: number;
  highlight: boolean;
  sort_order: number;
};

export default function PaymentRequired() {
  const navigate = useNavigate();
  const { user, signOut, loading } = useAuth();
  const [billing, setBilling] = useState<Billing>("monthly");
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [checkoutLoadingPlan, setCheckoutLoadingPlan] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const rows = await listPricingTiers();
        setTiers(rows as any);
      } catch {
        setTiers([]);
      }
    })();
  }, []);

  // Prevent navigation away from this page
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const beginCheckout = async (tierId: string) => {
    setCheckoutLoadingPlan(tierId);
    setSelectedPlan(tierId);

    try {
      const session = await createCheckoutSession({
        pricingTierId: tierId,
        billingCycle: billing,
      });
      toast.success("Redirecting you to secure checkout...");

      // Redirect to Stripe checkout
      window.location.href = session.checkout_url;
    } catch (error) {
      console.error("Failed to create checkout session:", error);
      setCheckoutLoadingPlan(null);
      toast.error("Unable to start checkout. Please try again.");
    }
  };

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!user) {
      navigate("/login", { replace: true });
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen gradient-dark p-4">
      <div className="max-w-4xl mx-auto py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between gap-4">
            <img
              src="https://xnjrgbjrqhbqsfocowbt.supabase.co/storage/v1/object/public/Logos/image4.jpeg"
              alt="Frequent Health logo"
              className="h-10 w-auto"
            />

            <Button
              type="button"
              variant="ghost"
              onClick={signOut}
              disabled={loading}
              className="text-gray-light hover:text-white"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Log out
            </Button>
          </div>

          <div className="text-center mt-6">
            <h1 className="text-3xl md:text-4xl font-bold mb-2 text-white">
              Payment Required
            </h1>
            <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 max-w-2xl mx-auto mt-4">
              <p className="text-red-200 text-lg font-medium">
                Your subscription payment didn't go through. Please complete payment to continue.
              </p>
            </div>
          </div>
        </div>

        {/* Pricing Tiers Section */}
        <Card className="border-primary/20 shadow-neon-glow bg-dark-card">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="w-20 h-20 gradient-primary rounded-full flex items-center justify-center mx-auto mb-4 shadow-neon-glow">
                <SparklesIcon className="w-10 h-10 text-black" />
              </div>
              <h2 className="text-3xl font-bold mb-2 text-white">Choose Your Coaching Plan</h2>
              <p className="text-gray-light">Select the perfect plan to continue your fitness journey</p>
            </div>

            <div className="mb-6">
              <div className="flex justify-center mb-6">
                <div className="bg-dark-slate p-1 rounded-full inline-flex">
                  <button
                    onClick={() => setBilling("monthly")}
                    className={cn(
                      "px-4 py-2 rounded-full text-sm font-medium transition-all",
                      billing === "monthly"
                        ? "bg-primary text-black shadow-neon-glow"
                        : "text-gray-light hover:text-white"
                    )}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setBilling("annual")}
                    className={cn(
                      "px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2",
                      billing === "annual"
                        ? "bg-primary text-black shadow-neon-glow"
                        : "text-gray-light hover:text-white"
                    )}
                  >
                    <span>Annual</span>
                    <Badge variant="default" className="bg-strength text-white text-xs">
                      Save 40%
                    </Badge>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {tiers.map((t) => (
                  <div
                    key={t.id}
                    className={cn(
                      "relative rounded-2xl border bg-dark-card/80 backdrop-blur-sm p-6 flex flex-col",
                      t.highlight
                        ? "border-primary/50 shadow-neon-glow-strong"
                        : "border-primary/20 shadow-neon-glow",
                      selectedPlan === t.id && "ring-2 ring-primary/60"
                    )}
                  >
                    {t.highlight && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-black text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                        Most Popular
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-bold text-white">{t.name}</h3>
                      {selectedPlan === t.id && (
                        <div className="h-6 w-6 bg-primary rounded-full" />
                      )}
                    </div>
                    <p className="text-gray-light mt-1">{t.blurb}</p>
                    <div className="mt-4">
                      <span className="text-3xl font-black text-white">
                        ${billing === "monthly" ? t.price_monthly : t.price_annual}
                      </span>
                      <span className="text-gray-light ml-2">/ {billing}</span>
                    </div>
                    <ul className="mt-4 space-y-2 text-gray-light">
                      {t.features.map((f) => (
                        <li key={f} className="flex items-start gap-2">
                          <span className="mt-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-primary text-black text-[10px]">
                            ✓
                          </span>
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-6">
                      <Button
                        className={cn(
                          "w-full font-bold uppercase tracking-wider",
                          selectedPlan === t.id
                            ? "shadow-neon-glow"
                            : "shadow-neon-glow hover:scale-105 transition-all",
                          checkoutLoadingPlan === t.id && "opacity-80 cursor-wait"
                        )}
                        variant={selectedPlan === t.id ? "default" : "outline"}
                        disabled={!!checkoutLoadingPlan}
                        onClick={() => beginCheckout(t.id)}
                      >
                        {checkoutLoadingPlan === t.id
                          ? "Redirecting..."
                          : selectedPlan === t.id
                            ? "Selected"
                            : "Choose Plan"}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {selectedPlan && (
              <div className="p-5 bg-primary/10 rounded-xl border border-primary/30 animate-fade-in">
                <div className="flex items-start gap-4">
                  <div className="bg-primary/20 p-3 rounded-full">
                    <Sparkles className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1 text-white">
                      Selected: {tiers.find((t) => t.id === selectedPlan)?.name}{" "}
                      {billing === "annual" ? "(Annual)" : "(Monthly)"}
                    </h4>
                    <p className="text-gray-light mb-1">
                      {tiers.find((t) => t.id === selectedPlan)?.blurb}
                    </p>
                    <p className="text-sm text-primary font-medium">
                      {billing === "annual" ? "Billed annually" : "Billed monthly"}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 bg-mobility/20 p-4 rounded-lg border border-mobility/30">
              <div className="flex items-start gap-3">
                <div className="text-mobility mt-1">
                  <CalendarCheck className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="font-medium text-white">Money Back Guarantee</h4>
                  <p className="text-sm text-gray-light">
                    Try risk-free for 14 days. If you're not satisfied, we'll refund your payment.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm text-gray-light mt-8 pt-6 border-t border-primary/20">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                <span>Secure payment</span>
              </div>
              <a
                href="/pricing"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:text-lime-hover"
              >
                Learn more about our plans
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

